import {
  useState,
  useReducer,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { Box } from "@mui/material";
import moment from "moment";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useOutletContext, useParams } from "react-router";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useIntl } from "react-intl";
import { formatPercentage } from "features/helpers/utils";
import _ from "lodash";

const useCommunity = () => {
  const { subEntityName } = useParams();
  const { formatMessage } = useIntl();

  const [entityName] = useOutletContext();
  const [startDate, setStartDate] = useState("");
  const [timeFrame, setTimeFrame] = useState("days");
  const [endDate, setEndDate] = useState("");
  const [dateRange, setDateRange] = useState([
    moment().utc().startOf("day").subtract(6, "days").valueOf(),
    moment().utc().endOf("day").valueOf(),
  ]);
  const dispatchSnackbar = useDispatchSnackbar();

  const [basicData, setBasicData] = useState({ serverName: "", dataInfo: [] });
  const [basicDataLoading, setBasicDataLoading] = useState(false);

  const [participantsData, setParticipantsData] = useState([]);
  const [participantsDataLoading, setParticipantsDataLoading] = useState(false);

  const [overViewParticipantsData, setOverViewParticipantsData] = useState([]);
  const [overViewParticipantsDataLoading, setOverViewParticipantsDataLoading] =
    useState(false);

  const [cohortData, setCohortData] = useState({});
  const [cohortDataLoading, setCohortDataLoading] = useState(false);

  const [messageData, setMessageData] = useState([]);
  const [messagesDataLoading, setMessagesDataLoading] = useState(false);

  const [reactionData, setReactionData] = useState([]);
  const [reactionsDataLoading, setReactionsDataLoading] = useState(false);

  const [platformParticipantData, setPlatformParticipantData] = useState([]);
  const [
    platformParticipantDataLoading,
    setPlatformParticipantDataDataLoading,
  ] = useState(false);

  const [platformEngagementData, setPlatformEngagementData] = useState([]);
  const [platformEngagementDataLoading, setPlatformEngagementDataDataLoading] =
    useState(false);

  const [keywordData, setKeywordData] = useState({
    rank: [],
    cloud: [],
  });
  const [keywordDataLoading, setKeywordDataLoading] = useState(false);

  const [engagementGrowthData, setEngagementGrowthData] = useState({});
  const [engagementGrowthDataLoading, setEngagementGrowthDataLoading] =
    useState({});

  const [overViewKeywordCloudData, setOverViewKeywordCloudData] = useState([]);
  const [overViewKeywordCloudDataLoading, setOverViewKeywordCloudDataLoading] =
    useState(false);

  const [overViewKeywordTrendData, setOverViewKeywordTrendData] = useState([]);
  const [overViewKeywordTrendDataLoading, setOverViewKeywordTrendDataLoading] =
    useState(false);

  const [targetMentionsData, setTargetMentionsData] = useState(null);
  const [targetMentionsLoading, setTargetMentionsLoading] = useState(false);

  const [initialExcludeWords, setInitialExcludedWords] = useState([]);
  const [excludedWords, setExcludedWords] = useState([]);
  const [excludedWordsLoading, setExcludedWordsLoading] = useState(false);

  const [excludedUsers, setExcludedUsers] = useState([]);
  const [excludedUsersLoading, setExcludedUsersLoading] = useState(false);

  const dynamicApiUrl = useMemo(() => {
    switch (true) {
      case !!subEntityName:
        return `apiEvaluate`;
      default:
        return `api`;
    }
  }, [subEntityName]);

  const dynamicEntityName = useMemo(() => {
    switch (true) {
      case !!subEntityName:
        return `${entityName}/${subEntityName}`;
      default:
        return entityName;
    }
  }, [entityName, subEntityName]);

  const fetchBasicData = async ({ type, from, to }) => {
    try {
      setBasicDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/${type}/summary`,
        {
          from,
          to,
        }
      );

      setBasicData(data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setBasicDataLoading(false);
    }
  };

  const getTooltipText = useCallback(
    (id) => {
      switch (id) {
        case "newParticipants":
          return formatMessage({
            id: "metrics.newParticipants.tooltip",
          });
        case "engagements":
          return formatMessage({
            id: "metrics.engagements.tooltip",
          });
        case "retentionRate":
          return formatMessage({
            id: "metrics.retentionRate.tooltip",
          });

        case "unifiedRetentionRate":
          return formatMessage({
            id: "metrics.unifiedRetention.tooltip",
          });
        default:
          return "";
      }
    },
    [formatMessage]
  );

  const highLevelDataArray = useMemo(() => {
    return basicData?.dataInfo?.map(
      ({ id, dataTitle, number, totalNumber, rate }) => {
        const formattedNumber =
          id === "retentionRate" || id === "unifiedRetentionRate"
            ? formatPercentage(number)
            : number;

        return {
          dataTitle,
          tooltipText: getTooltipText(id),
          number: formattedNumber,
          totalNumber: totalNumber || undefined, // 若無此欄位則為 undefined
          rate,
          id,
        };
      }
    );
  }, [basicData?.dataInfo, getTooltipText]);

  const fetchParticipantsData = async ({ type, from, to }) => {
    try {
      setParticipantsDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/${type}/participants`,
        {
          from,
          to,
        }
      );

      setParticipantsData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setParticipantsDataLoading(false);
    }
  };

  const fetchMessageData = async ({ type, from, to }) => {
    try {
      setMessagesDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/${type}/messages`,
        {
          from,
          to,
        }
      );

      setMessageData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setMessagesDataLoading(false);
    }
  };

  const fetchReactionData = async ({ type, from, to }) => {
    try {
      setReactionsDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/${type}/reactions`,
        {
          from,
          to,
        }
      );
      setReactionData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setReactionsDataLoading(false);
    }
  };

  const fetchCohortData = async ({ type, from, to }) => {
    try {
      setCohortDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/${type}/cohort`,
        {
          from,
          to,
        }
      );
      setCohortData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setCohortDataLoading(false);
    }
  };

  const fetchKeywordData = async ({ type, from, to }) => {
    try {
      setKeywordDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/${type}/keywords`,
        {
          from,
          to,
        }
      );
      const excludedKeywords = await fetchExcludedKeywords({ platform: type });

      const excludedKeywordValues = excludedKeywords.map(
        (excluded) => excluded.keyword
      );

      let cloud = _.filter(
        _.get(data, "data.cloud", []),
        (c) => !excludedKeywordValues.includes(c.value)
      );

      let rank = _.filter(
        _.get(data, "data.rank", []),
        (r) => !excludedKeywordValues.includes(r.keyword)
      );

      setKeywordData({
        cloud,
        rank,
      });
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setKeywordDataLoading(false);
    }
  };

  const fetchNewParticipantsData = async ({ from, to }) => {
    try {
      setOverViewParticipantsDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/overview/newParticipants`,
        {
          from,
          to,
        }
      );
      setOverViewParticipantsData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setOverViewParticipantsDataLoading(false);
    }
  };

  const fetchEngagementsGrowthData = async ({ from, to }) => {
    try {
      setEngagementGrowthDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/overview/engagements`,
        {
          from,
          to,
        }
      );
      setEngagementGrowthData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setEngagementGrowthDataLoading(false);
    }
  };

  const fetchOverViewKeywordCloudData = async ({ from, to }) => {
    try {
      setOverViewKeywordCloudDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/overview/keywordCloud`,
        {
          from,
          to,
        }
      );
      setOverViewKeywordCloudData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setOverViewKeywordCloudDataLoading(false);
    }
  };

  const fetchOverViewKeywordTrend = async ({ from, to, keywordIds }) => {
    try {
      setOverViewKeywordTrendDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/overview/keywordTrend`,
        {
          from,
          to,
          keywordIds,
        }
      );
      setOverViewKeywordTrendData(data?.data?.trend);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setOverViewKeywordTrendDataLoading(false);
    }
  };

  const handleTargetMentionClose = () => {
    setTargetMentionsData(null);
  };

  const fetchMentionsDetail = async ({ type, keywordIds }) => {
    try {
      setTargetMentionsLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/communityDashboard/${dynamicEntityName}/${type}/mentions`,
        {
          keywordIds,
        }
      );

      let name = _.get(data, "data.name");
      let mentions = _.get(data, "data.mentions", []);

      const excludedSenderhandles = _.map(excludedUsers, (u) => u.userHandle);

      let updatedMentions = _.filter(
        mentions,
        (m) => !excludedSenderhandles.includes(m.senderHandle)
      );

      setTargetMentionsData({
        name,
        times: updatedMentions.length,
        mentions: updatedMentions,
      });
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setTargetMentionsLoading(false);
    }
  };

  const fetchExcludedKeywords = async ({ platform }) => {
    let result = [];
    try {
      setExcludedWordsLoading(true);
      let { data } = await axios.get(
        `${apiUrl}/api/communityDashboard/${entityName}/${platform}/getExcludedKeywords`
      );
      data = _.map(data, (d) => {
        return {
          ...d,
          label: d.keyword,
          value: d._id,
        };
      });
      setInitialExcludedWords(data);
      setExcludedWords(data);
      result = data;
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setExcludedWordsLoading(false);
      return result;
    }
  };

  const fetchExcludedUsers = async ({ platform }) => {
    let result = [];
    try {
      setExcludedUsersLoading(true);
      const { data } = await axios.get(
        `${apiUrl}/api/communityDashboard/${entityName}/${platform}/getExcludedUsers`
      );
      setExcludedUsers(data);
      result = data;
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setExcludedUsersLoading(false);
      return result;
    }
  };

  const manageKeywordFilter = async ({ platform, startDate, endDate }) => {
    try {
      setExcludedWordsLoading(true);

      let payload = {
        includedKeywords: [],
        excludedKeywords: [],
      };

      const includedWords = _.differenceBy(
        initialExcludeWords,
        excludedWords,
        "label"
      );
      const excludedWordsNew = _.differenceBy(
        excludedWords,
        initialExcludeWords,
        "label"
      );

      payload.includedKeywords = _.map(includedWords, (word) => word.label);
      payload.excludedKeywords = _.map(excludedWordsNew, (word) => word.label);

      if (includedWords.length > 0 || excludedWordsNew.length > 0) {
        await axios.post(
          `${apiUrl}/api/communityDashboard/${entityName}/${platform}/manageKeywords`,
          payload
        );
      }

      await fetchKeywordData({ type: platform, from: startDate, to: endDate });
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setExcludedWordsLoading(false);
    }
  };

  const manageUserFilter = async ({
    platform,
    userHandle,
    option,
    startDate,
    endDate,
  }) => {
    try {
      setExcludedWordsLoading(true);

      const payload = {
        userHandle,
        option,
      };

      await axios.post(
        `${apiUrl}/api/communityDashboard/${entityName}/${platform}/manageUsers`,
        payload
      );

      await fetchExcludedUsers({ platform });
      await fetchKeywordData({ type: platform, from: startDate, to: endDate });
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setExcludedWordsLoading(false);
    }
  };

  return {
    dateRange,
    setDateRange,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    highLevelDataArray,
    timeFrame,
    setTimeFrame,
    basicData,
    basicDataLoading,
    participantsData,
    participantsDataLoading,
    cohortData,
    cohortDataLoading,
    messageData,
    messagesDataLoading,
    reactionData,
    reactionsDataLoading,
    keywordData,
    keywordDataLoading,
    platformParticipantData,
    platformParticipantDataLoading,
    platformEngagementData,
    platformEngagementDataLoading,
    overViewParticipantsData,
    overViewParticipantsDataLoading,
    engagementGrowthData,
    engagementGrowthDataLoading,
    overViewKeywordCloudData,
    overViewKeywordCloudDataLoading,
    overViewKeywordTrendData,
    overViewKeywordTrendDataLoading,
    targetMentionsData,
    targetMentionsLoading,
    handleTargetMentionClose,
    initialExcludeWords,
    excludedWords,
    setExcludedWords,
    excludedWordsLoading,
    excludedUsers,
    setExcludedUsers,
    excludedUsersLoading,
    fetchBasicData,
    fetchParticipantsData,
    fetchMessageData,
    fetchReactionData,
    fetchCohortData,
    fetchKeywordData,
    fetchNewParticipantsData,
    fetchEngagementsGrowthData,
    fetchOverViewKeywordCloudData,
    fetchOverViewKeywordTrend,
    fetchMentionsDetail,
    fetchExcludedUsers,
    manageKeywordFilter,
    manageUserFilter,
  };
};

export default useCommunity;
